import { LoadState, useLoad } from "@redotech/react-util/load";
import { ValidatedConciergeTreatment } from "@redotech/redo-model/ab-tests/concierge";
import { ConciergeProductFormSettings } from "@redotech/redo-model/concierge-product-form/concierge-product-form";
import { TeamInfo } from "@redotech/redo-model/team";
import { Flex } from "@redotech/redo-web/flex";
import { createContext, memo } from "react";
import { PromptForm } from "./prompt-form";

const PRODUCT_FORM_ID = "product-form-with-custom-css";

export interface ConciergeProductFormProps {
  settings: ConciergeProductFormSettings;
  getTeamInfo: () => Promise<TeamInfo>;
  useConciergeTreatment: ({
    currentUrl,
  }: {
    currentUrl: URL;
  }) => LoadState<ValidatedConciergeTreatment | undefined>;
  useConciergeQuestions: ({
    currentUrl,
  }: {
    currentUrl: URL;
  }) => LoadState<{ questions: string[] }>;
  onConciergeLoaded: () => Promise<void>;
}

export const ConciergeTreatmentContext = createContext<
  ValidatedConciergeTreatment | undefined
>(undefined);

export const ConciergeProductForm = memo(function ConciergeProductForm({
  settings,
  getTeamInfo,
  useConciergeTreatment,
  useConciergeQuestions,
  onConciergeLoaded,
}: ConciergeProductFormProps) {
  const teamInfoLoad = useLoad(async () => getTeamInfo(), []);

  const conciergeTreatmentLoad = useConciergeTreatment({
    currentUrl: new URL(window.location.href),
  });

  if (
    !teamInfoLoad.value ||
    !teamInfoLoad.value.conciergeEnabled ||
    conciergeTreatmentLoad.pending ||
    (conciergeTreatmentLoad.value &&
      !conciergeTreatmentLoad.value.settings.productConciergeEnabled) ||
    // Firefox doesn't prevent rendering the form twice with the same id
    document.getElementById(PRODUCT_FORM_ID)
  ) {
    return null;
  }

  return (
    <ConciergeTreatmentContext.Provider value={conciergeTreatmentLoad.value}>
      <Flex
        dir="column"
        gap="lg"
        id={PRODUCT_FORM_ID}
        py={settings.addVerticalPadding ? "xl" : undefined}
        w="full"
      >
        <PromptForm
          onConciergeLoaded={onConciergeLoaded}
          settings={settings}
          storeName={teamInfoLoad.value.name}
          useConciergeQuestions={useConciergeQuestions}
        />
      </Flex>
      {settings.customCss && (
        <style>
          {`#${PRODUCT_FORM_ID} {
          ${settings.customCss}
        }`}
        </style>
      )}
    </ConciergeTreatmentContext.Provider>
  );
});
